// SQL Keywords and operators for autocomplete
export const SQL_KEYWORDS = [
  // DDL (Data Definition Language)
  'CREATE', 'DROP', 'ALTER', 'TRUNCATE',
  'CREATE TABLE', 'CREATE VIEW', 'CREATE INDEX', 'CREATE SCHEMA', 'CREATE DATABASE',
  'DROP TABLE', 'DROP VIEW', 'DROP INDEX', 'DROP SCHEMA', 'DROP DATABASE',
  'ALTER TABLE', 'ALTER VIEW', 'ALTER INDEX', 'ALTER SCHEMA', 'ALTER DATABASE',
  
  // DML (Data Manipulation Language)
  'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'MERGE', 'UPSERT',
  'INSERT INTO', 'UPDATE SET', 'DELETE FROM',
  
  // DQL (Data Query Language)
  'SELECT', 'FROM', 'WHERE', 'GROUP BY', 'HAVING', 'ORDER BY', 'LIMIT', 'OFFSET',
  'DISTINCT', 'ALL', 'TOP', 'UNION', 'UNION ALL', 'INTERSECT', 'EXCEPT',
  
  // DCL (Data Control Language)
  'GRANT', 'REVOKE', 'DENY',
  
  // TCL (Transaction Control Language)
  'COMMIT', 'ROLLBACK', 'SAVEPOINT', 'BEGIN', 'START TRANSACTION',
  
  // Clauses and Keywords
  'AS', 'ALIAS', 'WITH', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'IF', 'EXISTS', 'NOT EXISTS', 'NULL', 'NOT NULL', 'DEFAULT',
  'PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE', 'CHECK', 'CONSTRAINT',
  'INDEX', 'KEY', 'AUTO_INCREMENT', 'IDENTITY',
  
  // Joins
  'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN', 'FULL OUTER JOIN',
  'CROSS JOIN', 'NATURAL JOIN', 'LEFT OUTER JOIN', 'RIGHT OUTER JOIN',
  'ON', 'USING',
  
  // Operators
  'AND', 'OR', 'NOT', 'IN', 'NOT IN', 'BETWEEN', 'NOT BETWEEN',
  'LIKE', 'NOT LIKE', 'ILIKE', 'REGEXP', 'RLIKE', 'IS', 'IS NOT',
  'IS NULL', 'IS NOT NULL',
  
  // Comparison operators (these will be handled as symbols)
  '=', '!=', '<>', '<', '>', '<=', '>=',
  
  // Functions - Aggregate
  'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'STDDEV', 'VARIANCE',
  'COUNT(*)', 'COUNT(DISTINCT)', 'GROUP_CONCAT', 'STRING_AGG',
  
  // Functions - String
  'CONCAT', 'SUBSTRING', 'LENGTH', 'CHAR_LENGTH', 'UPPER', 'LOWER',
  'LTRIM', 'RTRIM', 'TRIM', 'LEFT', 'RIGHT', 'REPLACE', 'REGEXP_REPLACE',
  'SPLIT_PART', 'POSITION', 'CHARINDEX',
  
  // Functions - Date/Time
  'NOW', 'CURRENT_TIMESTAMP', 'CURRENT_DATE', 'CURRENT_TIME',
  'DATE', 'TIME', 'DATETIME', 'TIMESTAMP', 'YEAR', 'MONTH', 'DAY',
  'HOUR', 'MINUTE', 'SECOND', 'DATE_ADD', 'DATE_SUB', 'DATEDIFF',
  'DATE_FORMAT', 'TO_DATE', 'TO_CHAR', 'EXTRACT',
  
  // Functions - Math
  'ABS', 'CEIL', 'CEILING', 'FLOOR', 'ROUND', 'POWER', 'SQRT',
  'MOD', 'RANDOM', 'RAND',
  
  // Functions - Conditional
  'COALESCE', 'NULLIF', 'GREATEST', 'LEAST',
  
  // Window functions
  'OVER', 'PARTITION BY', 'ROW_NUMBER', 'RANK', 'DENSE_RANK',
  'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE',
  
  // Data types
  'VARCHAR', 'CHAR', 'TEXT', 'INTEGER', 'INT', 'BIGINT', 'SMALLINT', 'TINYINT',
  'DECIMAL', 'NUMERIC', 'FLOAT', 'DOUBLE', 'REAL',
  'DATE', 'TIME', 'DATETIME', 'TIMESTAMP', 'YEAR',
  'BOOLEAN', 'BOOL', 'BIT', 'BINARY', 'VARBINARY',
  'JSON', 'JSONB', 'XML', 'UUID',
  
  // Other common keywords
  'SHOW', 'DESCRIBE', 'DESC', 'EXPLAIN', 'ANALYZE',
  'USE', 'SET', 'DECLARE', 'CALL', 'EXECUTE',
  'PROCEDURE', 'FUNCTION', 'TRIGGER', 'VIEW', 'SEQUENCE',
  'TEMPORARY', 'TEMP', 'GLOBAL', 'LOCAL', 'SESSION',
];


// Context-aware keyword groups
export const SQL_KEYWORD_CONTEXTS = {
  afterSelect: ['DISTINCT', 'ALL', 'TOP', '*'],
  afterFrom: ['WHERE', 'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN', 'CROSS JOIN'],
  afterWhere: ['AND', 'OR', 'GROUP BY', 'HAVING', 'ORDER BY', 'LIMIT'],
  afterGroupBy: ['HAVING', 'ORDER BY', 'LIMIT'],
  afterHaving: ['ORDER BY', 'LIMIT'],
  afterOrderBy: ['ASC', 'DESC', 'LIMIT'],
  afterJoin: ['ON', 'USING'],
  inParentheses: ['SELECT', 'CASE', 'WHEN'],
};


// Function to get keyword suggestions based on partial input
export function getKeywordSuggestions(partial: string, maxResults = 10): string[] {
  const upperPartial = partial.toUpperCase();
  return SQL_KEYWORDS
    .filter(keyword => keyword.startsWith(upperPartial))
    .slice(0, maxResults);
}

// Function to extract table names from SQL query
export function extractTableNames(sql: string): string[] {
  const tableNames: string[] = [];
  const cleanSql = sql.replace(/--.*$/gm, '').replace(/\/\*[\s\S]*?\*\//g, ''); // Remove comments
  
  // Patterns to match table names after FROM, JOIN, INTO, UPDATE
  const patterns = [
    /FROM\s+([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)/gi,
    /JOIN\s+([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)/gi,
    /INTO\s+([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)/gi,
    /UPDATE\s+([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)/gi,
  ];
  
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(cleanSql)) !== null) {
      const tableName = match[1].trim();
      // Handle schema.table format, extract just the table name
      const parts = tableName.split('.');
      const actualTableName = parts.length > 1 ? parts[1] : parts[0];
      if (actualTableName && !tableNames.includes(actualTableName)) {
        tableNames.push(actualTableName);
      }
    }
  }
  
  return tableNames;
}

// Function to determine the current SQL context based on cursor position
export function getSqlContext(sql: string, cursorPosition: number): {
  context: string;
  currentWord: string;
  isAfterTableName: boolean;
  nearbyTableName?: string;
} {
  const textBeforeCursor = sql.substring(0, cursorPosition);
  const textAfterCursor = sql.substring(cursorPosition);
  
  // Find the current word being typed
  const beforeMatch = /[a-zA-Z_][a-zA-Z0-9_]*$/.exec(textBeforeCursor);
  const afterMatch = /^[a-zA-Z0-9_]*/.exec(textAfterCursor);
  const currentWord = (beforeMatch ? beforeMatch[0] : '') + (afterMatch ? afterMatch[0] : '');
  
  // Clean text for analysis (remove comments and extra spaces)
  const cleanText = textBeforeCursor
    .replace(/--.*$/gm, '')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  
  let context = 'general';
  let isAfterTableName;
  let nearbyTableName: string | undefined;
  
  // Determine context based on the last significant keywords
  if (/\bSELECT\s*$/.exec(cleanText)) {
    context = 'afterSelect';
  } else if (/\bFROM\s*$/.exec(cleanText)) {
    context = 'afterFrom';
  } else if (/\bWHERE\s*$/.exec(cleanText)) {
    context = 'afterWhere';
  } else if (/\bGROUP\s+BY\s*$/.exec(cleanText)) {
    context = 'afterGroupBy';
  } else if (/\bHAVING\s*$/.exec(cleanText)) {
    context = 'afterHaving';
  } else if (/\bORDER\s+BY\s*$/.exec(cleanText)) {
    context = 'afterOrderBy';
  } else if (/\b(?:INNER\s+|LEFT\s+|RIGHT\s+|FULL\s+|CROSS\s+)?JOIN\s*$/.exec(cleanText)) {
    context = 'afterJoin';
  }
  
  // Check if we're in a position where column names are expected
  const columnContexts = [
    /\bSELECT\s+(?:DISTINCT\s+)?(?:[a-zA-Z_][a-zA-Z0-9_]*\s*,\s*)*$/,
    /\bWHERE\s+(?:[a-zA-Z_][a-zA-Z0-9_]*\s*(?:=|!=|<>|<|>|<=|>=|LIKE|IN)\s*(?:'[^']*'|\d+)\s+(?:AND|OR)\s+)*$/,
    /\bGROUP\s+BY\s+(?:[a-zA-Z_][a-zA-Z0-9_]*\s*,\s*)*$/,
    /\bORDER\s+BY\s+(?:[a-zA-Z_][a-zA-Z0-9_]*\s*(?:ASC|DESC)?\s*,\s*)*$/,
  ];
  
  isAfterTableName = columnContexts.some(pattern => pattern.test(cleanText));
  
  // Try to find the nearest table name for column suggestions
  const fromMatches = Array.from(textBeforeCursor.matchAll(/\bFROM\s+([a-zA-Z_][a-zA-Z0-9_]*)/gi));
  const joinMatches = Array.from(textBeforeCursor.matchAll(/\b(?:INNER\s+|LEFT\s+|RIGHT\s+|FULL\s+|CROSS\s+)?JOIN\s+([a-zA-Z_][a-zA-Z0-9_]*)/gi));
  
  // Get the most recent table reference
  const allMatches = [...fromMatches, ...joinMatches];
  
  if (allMatches.length > 0) {
    const lastMatch = allMatches[allMatches.length - 1];
    const tableNameMatch = /(?:FROM|JOIN)\s+([a-zA-Z_][a-zA-Z0-9_]*)/i.exec(lastMatch[0]);
    if (tableNameMatch) {
      // eslint-disable-next-line @typescript-eslint/prefer-destructuring
      nearbyTableName = tableNameMatch[1];
    }
  }
  
  return {
    context,
    currentWord,
    isAfterTableName,
    nearbyTableName,
  };
}
